import React, { useState } from 'react';
import styled from 'styled-components';
import VerificationInput from 'react-verification-input';
import { useHistory } from 'react-router-dom';
import { Alert as ErrorAlert, Spinner } from 'react-bootstrap';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { verifyMFA } from '../../actions/auth';
import { AccountContentContainer } from './common';
import { Tooltip, withStyles } from "@material-ui/core";

import { ReactComponent as InfoTooltipIcon } from "../../assets/info-tooltip-icon.svg";


const StyledTooltip = (props) => {
  const { className, ...other } = props;

  return (
    <Tooltip
      {...other}
      interactive={true}
      enterDelay={0}
      leaveDelay={200} 
      PopperProps={{
        className: className,
        style: {
          backgroundColor: "#AEE3F8",
          marginBottom: "20px",
          borderRadius: "4px",
          zIndex: 3,
        },
      }}
    />
  );
};

const StyledTooltipWrapper = withStyles(() => ({
  tooltip: {
    backgroundColor: "transparent",
    color: "black!important",
    fontSize: "14px!important",
    fontWeight: 400 + "!important",
    width: "420px",
    padding: "10px",
    textAlign: "center",
    margin: "0px",
    position: "relative",
  },
}))(StyledTooltip);

  
const useStyles = makeStyles({
    container: {
        width: '100% !important',
        justifyContent: 'space-between',
    },
    character: {
        width: '48px !important',
        height: '52px',
        lineHeight: '52px',
        fontSize: '22px !important',
        backgroundColor: '#fff !important',
        border: '1px solid #00AEEF !important',
        borderRadius: '10px',
        marginLeft: '0px !important',
        color: '#0C2945',
        marginLeft: '8px'
    },
});

const Container = styled.div`
    max-width: 500px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const HeaderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const Heading = styled.h2`
    color: #0C2945;
    font-size: 24px;
    line-height: 32px;
    font-weight: 700;
    margin-bottom: 12px;
    text-align: center;
`;

const Text = styled.p`
    color: #5B6575;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 32px;
    text-align: center;
`;

const VerificationContainer = styled.div`
    width: 100%;
    margin-bottom: 24px;
`;

const SubmitButton = styled.button`
    width: 100%;
    height: 48px;
    border-radius: 12px;
    border: none;
    background: #00AEEF;
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const BackCodeLink = styled.button`
    background: none;
    border: none;
    color: #00AEEF;
    font-size: 14px;
    cursor: pointer;
    text-decoration: underline;
    display: block;
    width: 100%;
    text-align: center;
    margin-top: 24px;
    
    &:hover {
        opacity: 0.8;
    }
`;

const BackCodeContainer = styled.div`
    margin-top: 24px;
    padding: 20px;
    background: #F7FAFC;
    border-radius: 12px;
    border: 1px solid #E4EDF5;
`;

const BackCodeInput = styled.input`
    width: 100%;
    height: 48px;
    border: 1px solid #E4EDF5;
    border-radius: 12px;
    padding: 0 16px;
    font-size: 14px;
    margin-bottom: 16px;
    font-family: monospace;
    
    &:focus {
        outline: none;
        border-color: #00AEEF;
    }
`;

const BackCodeWarning = styled.div`
    background: #FFF8E6;
    border: 1px solid #FFE2A8;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 16px;
    font-size: 12px;
    color: #8A5B00;
`;

const BackCodeSubmitButton = styled.button`
    width: 100%;
    height: 40px;
    border-radius: 8px;
    border: none;
    background: #6c757d;
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
    
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SwitchLink = styled.button`
    background: none;
    border: none;
    color: #00AEEF;
    font-size: 14px;
    cursor: pointer;
    text-decoration: underline;
    display: block;
    width: 100%;
    text-align: center;
    margin-top: 16px;
`;

const Divider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    margin: 24px 0;
    
    &::before,
    &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #E4EDF5;
    }
    
    &::before {
        margin-right: 12px;
    }
    
    &::after {
        margin-left: 12px;
    }
`;

const DividerText = styled.span`
    color: #5B6575;
    font-size: 12px;
`;

function MultiFactorVerification() {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();
    const { tempToken } = useSelector(state => state.auth);
    
    const [verificationCode, setVerificationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showBackCode, setShowBackCode] = useState(false);
    const [backupCode, setBackupCode] = useState('');
    const [backupLoading, setBackupLoading] = useState(false);
    const userEmail = 'support@seewithin.co';

    React.useEffect(() => {
        if (!tempToken) {
            history.push('/login');
        }
    }, [tempToken, history]);

    const redirectUser = (currentUser) => {
        window.analytics.track('Login Success', { email: currentUser.email });
    
        if (currentUser.role === 'doctor') {
            history.push('/auth/appointments');
            return;
        }
        
        const isUserHaveUploads = currentUser.isHaveUploads;
    
        if (currentUser.role === 'radiology_admin' && isUserHaveUploads) {
            history.push('/auth/radAdmin/Overview');
            return;
        }
        
        if (currentUser.role === 'radiology_admin' && !isUserHaveUploads) {
            history.push('/auth/radAdmin/Dashboard');
            return;
        }
    
        history.push('/auth/dashboard');
    };

    const handleVerify = async () => {
        if (verificationCode.length !== 6) {
            setError('Please enter a valid 6-digit code');
            return;
        }

        setLoading(true);
        setError('');
        
        const result = await dispatch(verifyMFA(verificationCode, tempToken, false));
        
        if (result.success) {
            redirectUser(result.user);
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const handleBackupCodeVerify = async () => {
        if (!backupCode.trim()) {
            setError('Please enter a backup code');
            return;
        }

        const formattedBackupCode = backupCode.trim().toUpperCase().replace(/\s/g, '');

        setBackupLoading(true);
        setError('');
        
        const result = await dispatch(verifyMFA(formattedBackupCode, tempToken, true));
        
        if (result.success) {
            redirectUser(result.user);
        } else {
            setError(result.error || 'Invalid backup code. Please try again.');
        }
        
        setBackupLoading(false);
    };

    const toggleBackCode = () => {
        setShowBackCode(!showBackCode);
        setError('');
        setBackupCode('');
        setVerificationCode('');
    };

    return (
        <AccountContentContainer>
            <Container>
                {(loading || backupLoading) && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}

                {error && (
                    <ErrorAlert variant="danger" onClose={() => setError('')} dismissible>
                        {error}
                    </ErrorAlert>
                )}

                {!showBackCode ? (
                    <>
                    <HeaderContainer>

                        <Heading>
                            Two-Factor Authentication Required
                        </Heading>
                            <StyledTooltipWrapper title={<div>
                                        <strong>Need help with MFA?</strong><br />
                                        If you've lost access to your authenticator app or need to reset MFA,<br />
                                        please contact our support team at <strong>{userEmail}</strong>.
                                    </div>}>
                            <InfoTooltipIcon
                               style={{ cursor: "pointer", alignSelf: "center", marginBottom: "8px" }}
                            /></StyledTooltipWrapper>
                    </HeaderContainer>

                        <Text>
                            Please enter the 6-digit verification code from your authenticator app
                            to complete your login.
                        </Text>

                        <VerificationContainer>
                            <VerificationInput
                                classNames={{
                                    container: classes.container,
                                    character: classes.character,
                                }}
                                value={verificationCode}
                                onChange={(value) => setVerificationCode(value)}
                                autoFocus
                                placeholder=""
                                validChars="0-9"
                            />
                        </VerificationContainer>

                        <SubmitButton
                            onClick={handleVerify}
                            disabled={verificationCode.length !== 6 || loading}
                        >
                            {loading ? 'Verifying...' : 'Verify & Login'}
                        </SubmitButton>

                        <BackCodeLink onClick={toggleBackCode}>
                            Lost access to your authenticator? Use backup code
                        </BackCodeLink>
                    </>
                ) : (
                    <>
                        <Heading>
                            Enter Backup Code
                        </Heading>

                        <Text>
                            Enter one of your backup codes to access your account.
                            Each backup code can only be used once.
                        </Text>

                        <BackCodeContainer>
                            <BackCodeWarning>
                                ⚠️ Backup codes are single-use only. After using a backup code, 
                                it will be invalidated.
                            </BackCodeWarning>

                            <BackCodeInput
                                type="text"
                                placeholder="Enter backup code"
                                value={backupCode}
                                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                                autoFocus
                                disabled={backupLoading}
                            />

                            <BackCodeSubmitButton
                                onClick={handleBackupCodeVerify}
                                disabled={!backupCode.trim() || backupLoading}
                            >
                                {backupLoading ? 'Verifying...' : 'Verify with Backup Code'}
                            </BackCodeSubmitButton>

                            <Divider>
                                <DividerText>OR</DividerText>
                            </Divider>

                            <SwitchLink onClick={toggleBackCode}>
                                Use authenticator app instead
                            </SwitchLink>
                        </BackCodeContainer>
                    </>
                )}
            </Container>
        </AccountContentContainer>
    );
}

export default MultiFactorVerification;